import { ActionIcon, OverflowList } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCircleCheck, IconCopy, IconExclamationCircle } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ProjectName } from '@xver/components/Molecules';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import './InstanceListTable.scss';

import { useDisclosure } from '@mantine/hooks';
import { InstanceResponseModel } from '../../../../../../apis/v1/instances/model';
import { Tag } from '../../../../../../components/Atoms';
import {
    ConnectInstanceModal,
    DataTable,
    DataTableColumn,
    toConnectTarget,
} from '@xver/components/Organisms';
import { COPY_IP_COOLDOWN_MS, copyToClipboard } from '@xver/utils/clipboard';
import { formatDate } from '@xver/utils/date';
import { formatMemory } from '@xver/utils/units';
import { useCreateInstanceTagMutate } from '../../../../../../hooks/CloudComputeHooks/useCreateInstanceTagMutate';
import { useDeleteInstanceTagMutate } from '../../../../../../hooks/CloudComputeHooks/useDeleteInstanceTagMutate';
import { useDestroyInstanceMutate } from '../../../../../../hooks/CloudComputeHooks/useDestoryInstanceMutate';
import {
    openInstanceConsole,
    useInstanceConsoleMutate,
} from '../../../../../../hooks/CloudComputeHooks/useInstanceConsoleMutate';
import { useInvokeActionMutate } from '../../../../../../hooks/CloudComputeHooks/useInvokeActionMutate';
import { useToggleBackupMutate } from '../../../../../../hooks/CloudComputeHooks/useToggleBackupMutate';
import { useUpdateInstanceMutate } from '../../../../../../hooks/CloudComputeHooks/useUpdateInstanceMutate';
import { useAppDispatch } from '../../../../../../hooks/useAppDispatch';
import { focusRowMenu, useMoveResource } from '../../../../../../hooks/useMoveResource';
import { setSelectedInstance } from '../../../../../../store/instance/instance.actions';
import { getOSFullName } from '../../../../../../utils/os';

import DestroyModal from './components/DestroyModal';
import EditProfileModal from './components/EditProfileModal';
import InstanceActionsMenu from './components/InstanceActionsMenu';
import InstanceAvatar from './components/InstanceAvatar';
import { useEditProfileForm } from './hooks/useEditProfileForm';
import {
    DEFAULT_STATUS_BADGE,
    getStatusText,
    NON_NAVIGABLE_STATES,
    STATUS_BADGE,
} from './InstanceListTable.utils';

interface InstanceListTableProps {
    instances?: InstanceResponseModel[];
    isLoading?: boolean;
}

export function InstanceListTable({ instances, isLoading = false }: InstanceListTableProps) {
    const { t, i18n } = useTranslation('cloudCompute');
    const safeInstances = instances ?? [];
    const lastCopiedIpRef = useRef<{ ipAddress: string; copiedAt: number } | null>(null);
    const [loadingInstanceId, setLoadingInstanceId] = useState<number | null>(null);
    const [selectedDestroyInstanceId, setSelectedDestroyInstanceId] = useState<number | null>(null);
    const [selectedEditInstanceId, setSelectedEditInstanceId] = useState<number | null>(null);
    const [selectedConnectInstanceId, setSelectedConnectInstanceId] = useState<number | null>(null);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const [editProfileModalOpened, { open: openEditProfileModal, close: closeEditProfileModal }] =
        useDisclosure(false);
    const [destroyModalOpened, { open: openDestroyModal, close: closeDestroyModal }] =
        useDisclosure(false);
    const [connectModalOpened, { open: openConnectModal, close: closeConnectModal }] =
        useDisclosure(false);

    const instanceConsoleMutation = useInstanceConsoleMutate();
    const toggleBackupMutation = useToggleBackupMutate();
    const invokeActionMutation = useInvokeActionMutate();
    const destroyInstanceMutation = useDestroyInstanceMutate();
    const updateInstanceMutation = useUpdateInstanceMutate();
    const createInstanceTagMutation = useCreateInstanceTagMutate();
    const deleteInstanceTagMutation = useDeleteInstanceTagMutate();
    const { canMove, requestMove, moveModal } = useMoveResource('instance');

    const {
        instanceNameValue,
        instanceNameError,
        instanceTagsValue,
        handleInstanceNameChange,
        setInstanceTagsValue,
        resetForm,
        handleSaveChanges,
    } = useEditProfileForm({ instances: safeInstances });

    const mutationStates = [
        instanceConsoleMutation,
        toggleBackupMutation,
        invokeActionMutation,
        destroyInstanceMutation,
        updateInstanceMutation,
        createInstanceTagMutation,
        deleteInstanceTagMutation,
    ];

    const isAnyMutationPending = mutationStates.some((m) => m.isPending);

    useEffect(() => {
        if (!isAnyMutationPending && loadingInstanceId !== null) {
            setLoadingInstanceId(null);
        }
    }, [isAnyMutationPending, loadingInstanceId]);

    useEffect(() => {
        if (editProfileModalOpened) {
            resetForm(selectedEditInstanceId);
        }
    }, [editProfileModalOpened, selectedEditInstanceId, resetForm]);

    const handleRowClick = useCallback(
        (instance: InstanceResponseModel) => {
            dispatch(setSelectedInstance(instance));
            navigate(`/cloud-compute/${instance.id}`);
        },
        [dispatch, navigate]
    );

    const handleCopyIpAddress = useCallback(
        async (ipAddress: string) => {
            const previousCopy = lastCopiedIpRef.current;
            const now = Date.now();

            if (
                previousCopy &&
                previousCopy.ipAddress === ipAddress &&
                now - previousCopy.copiedAt < COPY_IP_COOLDOWN_MS
            ) {
                return;
            }

            try {
                await copyToClipboard(ipAddress);
                lastCopiedIpRef.current = { ipAddress, copiedAt: now };
                notifications.show({
                    title: t('notifications:ipAddress.copied'),
                    message: t('notifications:ipAddress.copiedMessage', { ipAddress }),
                    icon: <IconCircleCheck size={32} color="oklch(72.3% 0.219 149.579)" />,
                });
            } catch {
                notifications.show({
                    title: t('notifications:ipAddress.copyFailed'),
                    message: t('notifications:ipAddress.copyFailedMessage', { ipAddress }),
                    icon: <IconExclamationCircle size={32} color="#eb5757" />,
                });
            }
        },
        [t]
    );

    const closeEditModal = useCallback(() => {
        if (selectedEditInstanceId !== null) focusRowMenu('instance', selectedEditInstanceId);
        setSelectedEditInstanceId(null);
        closeEditProfileModal();
    }, [closeEditProfileModal, selectedEditInstanceId]);

    const cancelDestroy = useCallback(() => {
        if (selectedDestroyInstanceId !== null) focusRowMenu('instance', selectedDestroyInstanceId);
        closeDestroyModal();
    }, [selectedDestroyInstanceId, closeDestroyModal]);

    const handleDestroyConfirm = useCallback(() => {
        if (selectedDestroyInstanceId === null) return;
        destroyInstanceMutation.mutate(selectedDestroyInstanceId);
        closeDestroyModal();
        focusRowMenu('instance', selectedDestroyInstanceId, true);
    }, [selectedDestroyInstanceId, destroyInstanceMutation, closeDestroyModal]);

    const destroyTargetName =
        safeInstances.find((i) => i.id === selectedDestroyInstanceId)?.hostname ?? '';

    const connectTarget = safeInstances.find((i) => i.id === selectedConnectInstanceId) ?? null;

    const closeConnectModalAndReset = useCallback(() => {
        if (selectedConnectInstanceId !== null) focusRowMenu('instance', selectedConnectInstanceId);
        setSelectedConnectInstanceId(null);
        closeConnectModal();
    }, [closeConnectModal, selectedConnectInstanceId]);

    const makeRowActions = useCallback(
        (instance: InstanceResponseModel) => ({
            onHowToConnect: () => {
                setSelectedConnectInstanceId(instance.id);
                openConnectModal();
            },
            onOpenConsole: () => {
                if (openInstanceConsole(instance, instanceConsoleMutation)) {
                    setLoadingInstanceId(instance.id);
                }
            },
            onToggleBackup: () => {
                setLoadingInstanceId(instance.id);
                toggleBackupMutation.mutate(instance.id);
            },
            onEditProfile: () => {
                setSelectedEditInstanceId(instance.id);
                openEditProfileModal();
            },
            onMove: () => requestMove({ id: String(instance.id), name: instance.hostname }),
            onReboot: () => {
                setLoadingInstanceId(instance.id);
                invokeActionMutation.mutate({ id: instance.id, action: 'reboot' as any });
            },
            onPowerOff: () => {
                setLoadingInstanceId(instance.id);
                invokeActionMutation.mutate({ id: instance.id, action: 'shutdown' as any });
            },
            onPowerOn: () => {
                setLoadingInstanceId(instance.id);
                invokeActionMutation.mutate({ id: instance.id, action: 'start' as any });
            },
            onDestroy: () => {
                setSelectedDestroyInstanceId(instance.id);
                openDestroyModal();
            },
        }),
        [
            instanceConsoleMutation,
            toggleBackupMutation,
            invokeActionMutation,
            openEditProfileModal,
            openDestroyModal,
            openConnectModal,
            requestMove,
        ]
    );

    const columns: DataTableColumn<InstanceResponseModel>[] = [
        {
            key: 'hostname',
            label: t('table.hostname'),
            render: (instance) => {
                const statusText = getStatusText(instance.state.power, t);
                const badge = STATUS_BADGE[instance.state.power] ?? DEFAULT_STATUS_BADGE;
                return (
                    <div className="flex flex-row justify-start items-center gap-3">
                        <InstanceAvatar
                            hostname={instance.hostname}
                            status={instance.state.power}
                        />
                        <div className="h-full flex flex-col justify-center items-start">
                            <h3 className="mt-0.5 text-base font-bold">
                                {NON_NAVIGABLE_STATES.includes(instance.state.power) ? (
                                    instance.hostname
                                ) : (
                                    <Link
                                        to={`/cloud-compute/${instance.id}`}
                                        className="hover:underline"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            dispatch(setSelectedInstance(instance));
                                        }}
                                    >
                                        {instance.hostname}
                                    </Link>
                                )}
                                <span
                                    className="ml-2 px-1.5 py-0.5 text-xs font-medium rounded"
                                    style={{
                                        backgroundColor: badge.bg,
                                        color: badge.fg,
                                    }}
                                >
                                    {statusText}
                                </span>
                            </h3>
                            <div className="flex flex-row justify-start items-center gap-1.5 text-sm flex-nowrap whitespace-nowrap text-fg/68">
                                <span>{formatMemory(instance.spec.memory)}</span>
                                <span className="text-3xl">·</span>
                                <span>{instance.spec.cpu} vCPU</span>
                                <span className="text-3xl">·</span>
                                <span>{instance.spec.disk} GB</span>
                                <span className="text-3xl">·</span>
                                <span>{getOSFullName(instance.image.slug)}</span>
                                {instance.state.backup === 'enabled' && (
                                    <>
                                        <span className="text-3xl">·</span>
                                        <span className="text-fg/80">
                                            {t('table.backupEnabled')}
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                );
            },
        },
        {
            key: 'project',
            label: t('projects:switcher.label'),
            render: (instance) => <ProjectName projectId={instance.projectId} />,
        },
        {
            key: 'ip',
            label: t('table.ipAddress'),
            render: (instance) => {
                const privateIp = instance.network.privateIpAddress;
                return (
                    <div className="flex flex-col items-start whitespace-nowrap">
                        <span className="text-base flex-nowrap">
                            {instance.network.ipAddress}
                            <ActionIcon
                                variant="transparent"
                                aria-label={t('table.copyIpAddress')}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    void handleCopyIpAddress(instance.network.ipAddress);
                                }}
                            >
                                <IconCopy size="16" className="text-fg/68" />
                            </ActionIcon>
                        </span>
                        {privateIp ? (
                            <span className="text-sm text-fg/68 flex-nowrap">
                                {privateIp}
                                <ActionIcon
                                    variant="transparent"
                                    aria-label={t('table.copyPrivateIpAddress')}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        void handleCopyIpAddress(privateIp);
                                    }}
                                >
                                    <IconCopy size="14" className="text-fg/68" />
                                </ActionIcon>
                            </span>
                        ) : (
                            <span className="text-sm text-fg/68">—</span>
                        )}
                    </div>
                );
            },
        },
        {
            key: 'created',
            label: t('table.created'),
            render: (instance) => (
                <span className="text-base text-fg/68 flex-nowrap whitespace-nowrap">
                    {formatDate(instance.createdAt, i18n.language, t)}
                </span>
            ),
        },
        {
            key: 'tags',
            label: t('table.tags'),
            render: (instance) =>
                instance.tags.length > 0 ? (
                    <OverflowList
                        data={instance.tags}
                        gap={6}
                        maxVisibleItems={2}
                        renderOverflow={(items) => <Tag size="sm" label={`+${items.length}`} />}
                        renderItem={(item, idx) => <Tag index={idx} size="sm" label={item.name} />}
                    />
                ) : (
                    <span className="text-fg/68 text-nowrap">{t('table.noTags')}</span>
                ),
        },
    ];

    return (
        <>
            <DataTable
                className="instance-table"
                rows={safeInstances}
                isLoading={isLoading}
                columns={columns}
                onRowClick={handleRowClick}
                isRowClickable={(instance) => !NON_NAVIGABLE_STATES.includes(instance.state.power)}
                actions={(instance) => (
                    <InstanceActionsMenu
                        instance={instance}
                        isLoading={loadingInstanceId === instance.id}
                        canManage={instance.access !== 'view'}
                        canMove={canMove}
                        {...makeRowActions(instance)}
                    />
                )}
                rowKey={(instance) => instance.id}
                skeletonRows={4}
                emptyMessage={t('table.noInstances')}
                caption={t('tabs.instances')}
                actionsLabel={t('table.actions')}
            />

            <EditProfileModal
                opened={editProfileModalOpened}
                instanceNameValue={instanceNameValue}
                instanceNameError={instanceNameError}
                instanceTagsValue={instanceTagsValue}
                isSaving={isAnyMutationPending}
                onClose={closeEditModal}
                onNameChange={handleInstanceNameChange}
                onTagsChange={setInstanceTagsValue}
                onSave={() =>
                    handleSaveChanges(
                        selectedEditInstanceId,
                        (id, hostname) => updateInstanceMutation.mutate({ id, data: { hostname } }),
                        (id, tag) => createInstanceTagMutation.mutate({ id, tag }),
                        (id, tagId) =>
                            deleteInstanceTagMutation.mutate({ id, tagId: tagId as any }),
                        closeEditModal
                    )
                }
            />

            <DestroyModal
                opened={destroyModalOpened}
                name={destroyTargetName}
                onClose={cancelDestroy}
                onConfirm={handleDestroyConfirm}
            />

            <ConnectInstanceModal
                target={connectTarget && toConnectTarget(connectTarget)}
                opened={connectModalOpened}
                onClose={closeConnectModalAndReset}
                onOpenConsole={
                    connectTarget?.access === 'view'
                        ? undefined
                        : () => {
                              if (connectTarget)
                                  openInstanceConsole(connectTarget, instanceConsoleMutation);
                          }
                }
            />

            {moveModal}
        </>
    );
}
